type PhysicalKeyEvent = {
  code?: string;
  shiftKey?: boolean;
  getModifierState?: (key: string) => boolean;
};

const UNSHIFTED_PUNCTUATION: Record<string, string> = {
  Minus: '-',
  Slash: '/',
};

/**
 * Resolve a reader keystroke from the physical key (`event.code`) instead of `event.key`.
 *
 * HID readers send US key positions; the Android keyboard layout then decides `event.key`
 * (e.g. Czech QWERTZ turns KeyY into `z`, Czech QWERTY turns Slash into `-`). `event.code`
 * is the scancode position and does not depend on the layout.
 *
 * Returns `undefined` when the physical key is not a supported code character or `code` is
 * empty — e.g. Zebra DataWedge keystroke output (scanCode=0) or InputEvents. Callers then
 * fall back to `event.key`, which goes through the layout `codeMap`.
 */
export function characterFromPhysicalKey(event: PhysicalKeyEvent) {
  const { code, shiftKey = false } = event;
  if (!code) {
    return undefined;
  }

  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) {
    // call on the event — native getModifierState must not be detached from it
    const capsLock = event.getModifierState?.('CapsLock') ?? false;
    return shiftKey !== capsLock ? letter[1] : letter[1].toLowerCase();
  }

  // Shifted digits/punctuation differ per layout (US `!`, Czech `1`) — leave them to `event.key`
  if (shiftKey) {
    return undefined;
  }

  const digit = /^Digit([0-9])$/.exec(code);
  if (digit) {
    return digit[1];
  }

  return UNSHIFTED_PUNCTUATION[code];
}
